import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquarePlus, X, Bug, Lightbulb, MessageCircle, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConsent } from "@/lib/consent";

const KINDS = [["problem", "Problem", Bug], ["idea", "Idea", Lightbulb], ["other", "Other", MessageCircle]];

const Form = ({ close }) => {
  const { user } = useAuth();
  const loc = useLocation();
  const [f, setF] = useState({ kind: "problem", message: "", email: "" });
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (f.message.trim().length < 3) { toast.error("Tell us a little more"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/feedback", { ...f, page: loc.pathname, viewport: `${window.innerWidth}x${window.innerHeight}`, user_agent: navigator.userAgent.slice(0, 300) });
      toast.success(`Thanks, feedback sent (ref ${data.reference})`);
      close();
    } catch (e2) { toast.error(errMsg(e2)); setBusy(false); }
  };
  return (
    <form onSubmit={send} className="fade-up w-[calc(100vw-2rem)] max-w-sm space-y-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl" data-testid="feedback-form">
      <div className="flex items-center justify-between"><h2 className="font-semibold text-[#0B192C]">Send feedback</h2><button type="button" onClick={close} aria-label="Close feedback" data-testid="feedback-close"><X className="h-4 w-4 text-slate-500" /></button></div>
      <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Feedback type">
        {KINDS.map(([k, l, I]) => <button key={k} type="button" role="radio" aria-checked={f.kind === k} onClick={() => setF({ ...f, kind: k })} data-testid={`feedback-kind-${k}`}
          className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-2 text-xs transition-colors ${f.kind === k ? "border-[#1F8A8B] bg-teal-50 font-semibold text-[#0B192C]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}><I className="h-4 w-4" />{l}</button>)}
      </div>
      <textarea rows={4} autoFocus aria-label="Your feedback" className="input" placeholder={f.kind === "problem" ? "What went wrong, and what did you expect?" : "What would make this better?"} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="feedback-message" />
      {!user && <input type="email" aria-label="Email (optional)" className="input" placeholder="Email (optional, if you'd like a reply)" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="feedback-email" />}
      <p className="text-xs text-slate-500">We'll include the page you're on ({loc.pathname}){user ? " and your account email" : ""}.</p>
      <button disabled={busy} className="btn-teal w-full justify-center" data-testid="feedback-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send feedback</button>
    </form>
  );
};

export const FeedbackButton = () => {
  const [open, setOpen] = useState(false);
  const consent = useConsent();
  if (!consent && !open) return null;
  return (
    <div className="fixed bottom-24 right-4 z-[60] flex flex-col items-end gap-3 sm:bottom-6" data-testid="feedback-widget">
      {open && <Form close={() => setOpen(false)} />}
      {!open && <button onClick={() => setOpen(true)} data-testid="feedback-button" aria-label="Send feedback"
        className="flex items-center gap-2 rounded-full bg-[#0B192C] px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"><MessageSquarePlus className="h-4 w-4" />Feedback</button>}
    </div>
  );
};
